import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorScreen } from 'src/components';
import { Loading } from 'src/components/Loading';
import { useClientDeliveryRequestTrackingFetcher } from 'src/hooks/useClientDeliveryRequestTrackingFetcher';
import { useComputeClientDeliveryRequests } from 'src/hooks/useComputeClientDeliveryRequests';
import { ClientRequestDeliveryStep } from './ClientRequestDeliveryStep';

export type Props = {
  token: string;
};

export const PublicClientDeliveryTracking: FC<Props> = ({ token }) => {
  const { t } = useTranslation();

  const { fetchData, isLoading, error, data } = useClientDeliveryRequestTrackingFetcher();

  const trackingSteps = useComputeClientDeliveryRequests(data?.history ?? []);

  const deleteAllNextStepsAfterCustomerReject = useMemo(() => {
    // Use findIndex to locate the index of the rejection step
    const rejectionStep = trackingSteps.findIndex((step) => {
      return step.isCompleted && step.title === 'REJECTED_BY_CUSTOMER';
    });

    if (rejectionStep > -1) {
      return trackingSteps.slice(0, rejectionStep + 1);
    }

    return trackingSteps;
  }, [trackingSteps]);

  useEffect(() => {
    fetchData(token);
  }, [fetchData, token]);

  const retryReload = useCallback(() => {
    fetchData(token);
  }, [fetchData, token]);

  if (isLoading) {
    return <Loading />;
  }

  if (error || !data) {
    return <ErrorScreen error={error ?? new Error('Aucune demande trouvé')} retry={retryReload} />;
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-50 py-10 px-4">
      {/* Header Section */}
      <div className="w-full max-w-xl bg-white rounded-lg shadow-md">
        <div className="bg-blue-600 text-white py-4 px-6 rounded-t-lg">
          <h1 className="text-lg font-semibold">{t('client-delivery-requests.page-title')}</h1>
        </div>
        <div className="p-6">
          <p
            className="mb-4 text-gray-700"
            dangerouslySetInnerHTML={{ __html: t('client-delivery-requests.hello', { name: data.name }) }}
          />
          <p className="text-gray-700 mb-6">{t('client-delivery-requests.you-will-informed-on-delivery-status')}</p>

          {/* Delivery Information */}
          <div className="bg-gray-100 rounded-md p-4 border-l-4 border-blue-500 mb-6">
            <p className="text-sm text-gray-600">
              <strong>Envoi Numéro :</strong> {data.shipping.tracker}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Numéro de la demande :</strong>
              {data.tracker}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Colis livré à l'agence :</strong> {data.shipping?.toAgency?.name}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Adresse de livraison :</strong> {data.address}
            </p>
          </div>

          {/* Timeline Section */}
          <div className="w-full max-w-xl mt-10">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">Avancement de la demande</h2>

            <div className="flex flex-col space-y-6 relative">
              <div className="absolute left-7 top-0 h-full w-0.5 bg-blue-500 opacity-10"></div>
              {deleteAllNextStepsAfterCustomerReject
                .filter((step) => step.isVisible)
                .filter((step) => {
                  // If "Organization Suggest Price" is completed, exclude "Rejected by Agency"
                  const isSuggestPriceCompleted = deleteAllNextStepsAfterCustomerReject.some(
                    (s) =>
                      (s.isCompleted && s.title === 'ORGANIZATION_SUGGEST_PRICE') ||
                      (s.isActive && s.title === 'ORGANIZATION_SUGGEST_PRICE'),
                  );
                  return !(isSuggestPriceCompleted && step.title === 'REJECTED_BY_AGENCY');
                })
                // not show REJECTED_BY_AGENCY if is not yet completed or active
                .filter((step) => {
                  if (step.title === 'REJECTED_BY_CUSTOMER') {
                    if (!step.isCompleted && !step.isActive) {
                      return false;
                    }
                  }

                  if (step.title === 'REJECTED_BY_AGENCY') {
                    if (!step.isCompleted && !step.isActive) {
                      return false;
                    }
                  }
                  return true;
                })
                .map((step, index) => (
                  <ClientRequestDeliveryStep
                    key={index}
                    {...step}
                    organizationNote={data.organizationSuggestedPriceNote}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
